/**
 * Relances configuration + runtime queue hooks.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';

/* ─── Relances config — Wave 5b Agent 5b.3 (FULLY IMPLEMENTED) ─────────── */

export interface RelancesConfigRow {
  id: string;
  userId: string;
  voiceId: string;
  enabled: boolean;
  catchupEnabled: boolean;
  catchupDelayDays: number;
  catchupMaxAttempts: number;
  catchupHint: string;
  projectEnabled: boolean;
  projectDelayDays: number;
  projectMaxAttempts: number;
  projectHint: string;
  lastShotEnabled: boolean;
  lastShotDelayDays: number;
  lastShotMaxAttempts: number;
  lastShotHint: string;
  createdAt: string;
  updatedAt: string;
}

export interface RelanceCustomTypeRow {
  id: string;
  userId: string;
  relancesConfigId: string;
  label: string;
  whenText: string;
  hint: string;
  delayDays: number;
  maxAttempts: number;
  enabled: boolean;
  createdAt: string;
}

export interface RelancesConfigResponse {
  config: RelancesConfigRow;
  customTypes: RelanceCustomTypeRow[];
}

export type RelancesConfigPatch = Partial<
  Omit<RelancesConfigRow, 'id' | 'userId' | 'voiceId' | 'createdAt' | 'updatedAt'>
>;

export interface RelanceCustomTypeInput {
  label: string;
  whenText?: string;
  hint?: string;
  delayDays?: number;
  maxAttempts?: number;
  enabled?: boolean;
}

export type RelanceCustomTypePatch = Partial<RelanceCustomTypeInput>;

const relancesConfigKey = (linkedinAccountId: string) =>
  ['relances-config', linkedinAccountId] as const;

/** `GET /api/v1/relances/:linkedinAccountId/config` */
export function useRelancesConfig(
  linkedinAccountId: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<RelancesConfigResponse, ApiError> {
  const client = useApiClient();
  return useQuery<RelancesConfigResponse, ApiError>({
    queryKey: linkedinAccountId
      ? relancesConfigKey(linkedinAccountId)
      : ['relances-config', '__none__'],
    queryFn: () =>
      getJson<RelancesConfigResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId ?? '')}/config`,
      ),
    enabled: !!linkedinAccountId && (options.enabled ?? true),
    retry: false,
  });
}

/** `PATCH /api/v1/relances/:linkedinAccountId/config` */
export function useUpdateRelancesConfig(
  linkedinAccountId: string,
): UseMutationResult<{ config: RelancesConfigRow }, ApiError, RelancesConfigPatch> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ config: RelancesConfigRow }, ApiError, RelancesConfigPatch>({
    mutationFn: (patch) =>
      patchJson<{ config: RelancesConfigRow }>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId)}/config`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: relancesConfigKey(linkedinAccountId) });
    },
  });
}

/** `useCustomRelanceTypes` — derived list reader (no separate endpoint). */
export function useCustomRelanceTypes(
  linkedinAccountId: string | undefined,
): UseQueryResult<RelanceCustomTypeRow[], ApiError> {
  const client = useApiClient();
  return useQuery<RelanceCustomTypeRow[], ApiError>({
    queryKey: linkedinAccountId
      ? ['relances-config', linkedinAccountId, 'customTypes']
      : ['relances-config', '__none__', 'customTypes'],
    queryFn: async () => {
      const response = await getJson<RelancesConfigResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId ?? '')}/config`,
      );
      return response.customTypes;
    },
    enabled: !!linkedinAccountId,
    retry: false,
  });
}

/** `POST /api/v1/relances/:linkedinAccountId/types` */
export function useCreateCustomRelanceType(
  linkedinAccountId: string,
): UseMutationResult<{ customType: RelanceCustomTypeRow }, ApiError, RelanceCustomTypeInput> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ customType: RelanceCustomTypeRow }, ApiError, RelanceCustomTypeInput>({
    mutationFn: (vars) =>
      postJson<{ customType: RelanceCustomTypeRow }>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId)}/types`,
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: relancesConfigKey(linkedinAccountId) });
      void qc.invalidateQueries({
        queryKey: ['relances-config', linkedinAccountId, 'customTypes'],
      });
    },
  });
}

/** `PATCH /api/v1/relances/:linkedinAccountId/types/:id` */
export function useUpdateCustomRelanceType(
  linkedinAccountId: string,
): UseMutationResult<
  { customType: RelanceCustomTypeRow },
  ApiError,
  { id: string; patch: RelanceCustomTypePatch }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { customType: RelanceCustomTypeRow },
    ApiError,
    { id: string; patch: RelanceCustomTypePatch }
  >({
    mutationFn: (vars) =>
      patchJson<{ customType: RelanceCustomTypeRow }>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId)}/types/${encodeURIComponent(vars.id)}`,
        vars.patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: relancesConfigKey(linkedinAccountId) });
      void qc.invalidateQueries({
        queryKey: ['relances-config', linkedinAccountId, 'customTypes'],
      });
    },
  });
}

/** `DELETE /api/v1/relances/:linkedinAccountId/types/:id` */
export function useDeleteCustomRelanceType(
  linkedinAccountId: string,
): UseMutationResult<{ ok: true }, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(
        client,
        `/api/v1/relances/${encodeURIComponent(linkedinAccountId)}/types/${encodeURIComponent(vars.id)}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: relancesConfigKey(linkedinAccountId) });
      void qc.invalidateQueries({
        queryKey: ['relances-config', linkedinAccountId, 'customTypes'],
      });
    },
  });
}

/* ─── Relances runtime — Wave 5d Agent 5d.3 (FULLY IMPLEMENTED) ────────── */
//
// Pending queue + per-row actions for `/relances/pending`. Realtime
// invalidation is the caller's responsibility — wire `useUserHubChannel`
// for `relance.cancelled` / `relance.sent` / `relance.scheduled` and call
// `invalidatePendingRelances(qc)` from the listener.

export type RelanceTypeKind = 'catchup' | 'project' | 'lastShot' | 'custom' | string;

export type RelanceState =
  | 'queued'
  | 'sent'
  | 'cancelled_manual_reply'
  | 'cancelled_user'
  | 'errored';

export interface PendingRelanceRow {
  id: string;
  conversationId: string;
  voiceId: string;
  typeKind: RelanceTypeKind;
  attemptNumber: number;
  state: RelanceState;
  scheduledFor: string;
  bodyDraft: string | null;
  createdAt: string;
  conversation: {
    id: string;
    linkedinAccountId: string;
    contactName: string;
    contactInitials: string;
    contactColor: string | null;
    contactRole: string | null;
    contactCompany: string | null;
    contactAvatarUrl: string | null;
    lastMessagePreview: string | null;
    lastMessageAt: string | null;
    temperature: 'cold' | 'warm' | 'hot' | null;
  };
}

export interface PendingRelancesResponse {
  pending: PendingRelanceRow[];
}

const pendingRelancesKey = ['relances', 'pending'] as const;

/** Imperative cache invalidation — used by realtime listeners. */
export function invalidatePendingRelances(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: pendingRelancesKey });
}

/** `GET /api/v1/relances/pending` */
export function usePendingRelances(
  options: { enabled?: boolean } = {},
): UseQueryResult<PendingRelancesResponse, ApiError> {
  const client = useApiClient();
  return useQuery<PendingRelancesResponse, ApiError>({
    queryKey: pendingRelancesKey,
    queryFn: () => getJson<PendingRelancesResponse>(client, '/api/v1/relances/pending'),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

export interface ApproveRelanceResponse {
  relance: { id: string; state: 'sent'; sentMessageId: string; resolvedAt: string };
}

/** `POST /api/v1/relances/:id/approve` */
export function useApproveRelance(): UseMutationResult<
  ApproveRelanceResponse,
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ApproveRelanceResponse, ApiError, { id: string }>({
    mutationFn: (vars) =>
      postJson<ApproveRelanceResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(vars.id)}/approve`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingRelancesKey });
    },
  });
}

/** `POST /api/v1/relances/:id/retry` */
export function useRetryRelance(): UseMutationResult<
  ApproveRelanceResponse,
  ApiError,
  { id: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<ApproveRelanceResponse, ApiError, { id: string }>({
    mutationFn: (vars) =>
      postJson<ApproveRelanceResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(vars.id)}/retry`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingRelancesKey });
    },
  });
}

export interface SkipRelanceResponse {
  relance: { id: string; state: 'cancelled_user'; resolvedAt: string };
}

/** `POST /api/v1/relances/:id/skip` */
export function useSkipRelance(): UseMutationResult<SkipRelanceResponse, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<SkipRelanceResponse, ApiError, { id: string }>({
    mutationFn: (vars) =>
      postJson<SkipRelanceResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(vars.id)}/skip`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingRelancesKey });
    },
  });
}

export interface SnoozeRelanceResponse {
  relance: { id: string; state: 'queued'; scheduledFor: string };
}

/** `POST /api/v1/relances/:id/snooze` */
export function useSnoozeRelance(): UseMutationResult<
  SnoozeRelanceResponse,
  ApiError,
  { id: string; snoozeUntil: string }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<SnoozeRelanceResponse, ApiError, { id: string; snoozeUntil: string }>({
    mutationFn: (vars) =>
      postJson<SnoozeRelanceResponse>(
        client,
        `/api/v1/relances/${encodeURIComponent(vars.id)}/snooze`,
        { snoozeUntil: vars.snoozeUntil },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendingRelancesKey });
    },
  });
}
